import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { PageContainer } from '@ge/components/containers';
import { placements } from '@ge/components/menu';
import { DraggableTable, DynamicTable } from '@ge/components/table';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import { Tooltip } from '@ge/components/tooltip';
import { DateTimeFormats } from '@ge/models/constants';
import { elevations } from '@ge/tokens/elevations';

import { useExistingGamageDetailsTable } from '../../data-hooks/use-existing-damage-details-table';
import {
  ExistingDamageDetailsColumnDefs,
  ExistingDamageDetailsColumns,
} from '../../models/existing-damage-details-col';

//const MetricCell = styled.div``;

const DescriptionContainer = styled.div`
  display: flex;
  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StatusContainer = styled.div`
  color: white;
  z-index: 1;
  background: #445a6c;
  padding: 4px 4px 4px 4px;
  border-radius: 10px;
  width: 2rem;
  font-size: 10px;
  height: auto;
  text-align: center;
  margin-left: -2px;
`;

// Component Table
export const ExistingDamageDetailsTable = ({
  columns,
  isLoading,
  data,
  sortAction,
  sortMetric,
  sortDirection,
  onDrop,
  scrollable,
  draggable,
  existingDamage,
  setExistingDamage,
}) => {
  // Translation keys
  const { t, ready } = useTranslation(['inspections.existing-damage-details'], {
    useSuspense: false,
  });

  const { checkedRows, renderCheckbox } = useExistingGamageDetailsTable({
    data,
  });

  useEffect(() => {
    const result = data.length ? data.filter(({ id }) => checkedRows.includes(id)) : [];
    setExistingDamage(result);
  }, [checkedRows, data, setExistingDamage]);

  // Table State / handlers
  const sortedDirection = useCallback(
    (metric) => (metric === sortMetric ? sortDirection : ''),
    [sortMetric, sortDirection],
  );

  /**
   * Function used by useTableFactories to bootstrap the cell values map
   * before returning the factory to process cells for that row. This should
   * contain values for all columns that should be expected to show data in
   * the table.
   */
  const cellValueMapFn = (cellValue) => {
    if (!cellValue) return {};

    return {
      [ExistingDamageDetailsColumns.SELECTED]: cellValue?.id,
      [ExistingDamageDetailsColumns.STATUS]: cellValue?.status,
      [ExistingDamageDetailsColumns.CATEGORY]: cellValue?.category,
      [ExistingDamageDetailsColumns.COMPONENT]: cellValue?.component,
      [ExistingDamageDetailsColumns.LOCATION]: cellValue?.location,
      [ExistingDamageDetailsColumns.PART]: cellValue?.part,
      [ExistingDamageDetailsColumns.DAMAGE]: cellValue?.damage,
      [ExistingDamageDetailsColumns.CREATION_DATE]: cellValue?.creationDate,
    };
  };

  /**
   * Factory function to generate custom table cell components in
   * the case that the contents are not simply primitives.
   */
  const customCellFn = useCallback(
    (columnKey, cellValue) => {
      switch (columnKey) {
        case ExistingDamageDetailsColumns.SELECTED:
          return renderCheckbox(cellValue);
        case ExistingDamageDetailsColumns.STATUS:
          return <StatusContainer>{cellValue.toUpperCase()}</StatusContainer>;
        case ExistingDamageDetailsColumns.CATEGORY:
        case ExistingDamageDetailsColumns.COMPONENT:
        case ExistingDamageDetailsColumns.LOCATION:
        case ExistingDamageDetailsColumns.PART:
        case ExistingDamageDetailsColumns.DAMAGE:
          return (
            <DescriptionContainer>
              <Tooltip title={cellValue} zIndex={elevations.P101} placement={placements.TOP}>
                <span>{cellValue}</span>
              </Tooltip>
            </DescriptionContainer>
          );
        case ExistingDamageDetailsColumns.CREATION_DATE:
          return (
            <DescriptionContainer>
              <Tooltip
                title={dayjs(cellValue * 1000).format(DateTimeFormats.DEFAULT_DATE)}
                zIndex={elevations.P101}
                placement={placements.TOP}
              >
                <span>
                  {cellValue ? dayjs(cellValue * 1000).format(DateTimeFormats.DEFAULT_DATE) : null}
                </span>
              </Tooltip>
            </DescriptionContainer>
          );
        default:
          return null;
      }
    },
    [renderCheckbox],
  );

  /**
   * Bootstrap table factories
   */
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: ExistingDamageDetailsColumnDefs,
    cellValueMapFn,
    customCellFn,
    sortAction,
    sortedDirection,
    draggable,
  });

  if (!columns || columns.length === 0) return null;

  const dynamicProps = {
    scrollable,
    compressed: true,
    transparent: true,
    noTitles: true,
    columns,
    columnGroupFactory,
    columnFactory,
    cellFactory,
    sortAction,
    values: data,
    onValueSelect: () => null,
    rowKeyProperty: 'selected',
    rowKey: 'id',
    dropHandler: draggable ? onDrop : () => null,
    rowsSelected: [...checkedRows],
    isLoading,
  };

  const TABLE = draggable ? <DraggableTable /> : <DynamicTable {...dynamicProps} />;

  return <PageContainer i18nReady={ready}>{TABLE}</PageContainer>;
};

ExistingDamageDetailsTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  data: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  sortAction: PropTypes.func,
  sortMetric: PropTypes.string,
  sortDirection: PropTypes.string,
  onDrop: PropTypes.func,
  scrollable: PropTypes.bool,
  draggable: PropTypes.bool,
  isLoading: PropTypes.bool,
  existingDamage: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  setExistingDamage: PropTypes.func.isRequired,
};

ExistingDamageDetailsTable.defaultProps = {
  sortAction: () => null,
  sortMetric: '',
  sortDirection: '',
  onDrop: () => null,
  scrollable: false,
  draggable: false,
  isLoading: false,
};
